import React, { FormEvent, useEffect, useLayoutEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ComputerDesktopIcon,
  DocumentTextIcon,
  FolderOpenIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../i18n';
import { homePathFor } from '../i18n/localePath';
import {
  fromPriceLabel,
  getContentTestingCounterpart,
  getContentTestingCurrencyCounterpart,
  getContentTestingFromPath,
  withFromPrice,
} from '../content-testing/routes';
import { scrollToTopInstant } from './ScrollToTop';

const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

const IRAINBOW_WEB = 'https://app.irainbow.co.za/';
const IRAINBOW_ORG = 'https://www.irainbow.co.za/';
const BOOKING_EMAIL = 'info@tamerin.tech';

const scrollToApply = (event?: React.MouseEvent<HTMLAnchorElement>) => {
  const target = document.getElementById('apply');
  if (!target) return;
  event?.preventDefault();
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const ContentTestingApplyForm: React.FC = () => {
  const { t, language } = useLanguage();
  const location = useLocation();
  const match = getContentTestingFromPath(location.pathname);
  const form = t.contentTesting.form;
  const [submitted, setSubmitted] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [role, setRole] = useState('');
  const [platformUrl, setPlatformUrl] = useState('');
  const [access, setAccess] = useState(form.accessOptions[0].value);
  const [scope, setScope] = useState('');

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedOrg = organisation.trim();
    if (!trimmedName || !trimmedEmail || !trimmedOrg) return;

    const roleLabel = form.roles.find((item) => item.value === role)?.label || role;
    const accessLabel = form.accessOptions.find((item) => item.value === access)?.label || access;
    const subject =
      language === 'de'
        ? `Scan-Buchung: ${trimmedOrg}`
        : `Scan booking: ${trimmedOrg}`;
    const body = [
      language === 'de' ? 'Neue Scan-Buchungsanfrage' : 'New scan booking request',
      '',
      `${form.name}: ${trimmedName}`,
      `${form.email}: ${trimmedEmail}`,
      `${form.organisation}: ${trimmedOrg}`,
      roleLabel ? `${form.role}: ${roleLabel}` : null,
      platformUrl.trim() ? `${form.platformUrl}: ${platformUrl.trim()}` : null,
      `${form.access}: ${accessLabel}`,
      scope.trim() ? `${form.scope}: ${scope.trim()}` : null,
      '',
      match?.currency === 'EUR'
        ? `Page: ${location.pathname} (from ${fromPriceLabel('EUR')}, up to 5,000 content questions)`
        : `Page: ${location.pathname} (from ${fromPriceLabel('USD')}, up to 5,000 content questions)`,
    ]
      .filter(Boolean)
      .join('\n');

    window.location.href = `mailto:${BOOKING_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div
        className="rounded-2xl border-2 border-success-200 bg-success-50 p-8"
        data-testid="content-testing-form-success"
        role="status"
      >
        <div className="flex items-start gap-3">
          <CheckCircleIcon className="w-8 h-8 text-success-600 flex-shrink-0" aria-hidden="true" />
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{form.successTitle}</h3>
            <p className="text-gray-700">{form.successBody}</p>
          </div>
        </div>
      </div>
    );
  }

  const fieldClass =
    'w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent';

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl border-2 border-primary-200 shadow-lg p-6 md:p-8 space-y-5"
      data-testid="content-testing-form"
      noValidate
    >
      <h3 className="text-2xl font-bold text-gray-900">{form.title}</h3>
      <p className="text-sm text-gray-500">{form.requiredNote}</p>

      <div className="grid md:grid-cols-2 gap-5">
        <label className="block">
          <span className="block text-sm font-semibold text-gray-800 mb-2">{form.name}</span>
          <input
            type="text"
            name="name"
            autoComplete="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={fieldClass}
          />
        </label>
        <label className="block">
          <span className="block text-sm font-semibold text-gray-800 mb-2">{form.email}</span>
          <input
            type="email"
            name="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={fieldClass}
          />
        </label>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <label className="block">
          <span className="block text-sm font-semibold text-gray-800 mb-2">{form.organisation}</span>
          <input
            type="text"
            name="organisation"
            autoComplete="organization"
            required
            value={organisation}
            onChange={(e) => setOrganisation(e.target.value)}
            className={fieldClass}
          />
        </label>
        <label className="block">
          <span className="block text-sm font-semibold text-gray-800 mb-2">{form.role}</span>
          <select
            name="role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className={fieldClass}
          >
            <option value="">{form.rolePlaceholder}</option>
            {form.roles.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="block">
        <span className="block text-sm font-semibold text-gray-800 mb-2">{form.platformUrl}</span>
        <input
          type="url"
          name="platformUrl"
          inputMode="url"
          placeholder="https://"
          value={platformUrl}
          onChange={(e) => setPlatformUrl(e.target.value)}
          className={fieldClass}
        />
      </label>

      <fieldset>
        <legend className="block text-sm font-semibold text-gray-800 mb-3">{form.access}</legend>
        <div className="space-y-2">
          {form.accessOptions.map((option) => (
            <label
              key={option.value}
              className="flex items-start gap-3 rounded-xl border border-gray-200 px-4 py-3 cursor-pointer hover:border-primary-300 hover:bg-primary-50/50"
            >
              <input
                type="radio"
                name="access"
                value={option.value}
                checked={access === option.value}
                onChange={() => setAccess(option.value)}
                className="mt-1"
              />
              <span className="text-gray-800">{option.label}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <label className="block">
        <span className="block text-sm font-semibold text-gray-800 mb-2">{form.scope}</span>
        <textarea
          name="scope"
          rows={4}
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          placeholder={form.scopePlaceholder}
          className={`${fieldClass} resize-y min-h-[6rem]`}
        />
      </label>

      <button
        type="submit"
        className="w-full bg-gradient-to-r from-primary-600 to-purple-600 text-white font-semibold px-6 py-4 rounded-xl hover:shadow-lg transition-all focus-visible"
      >
        {form.submit}
      </button>
    </form>
  );
};

/**
 * Landing page for automated learning-platform content testing.
 * /content-testing is English + USD; /content-testing-eu is English + EUR;
 * /lerninhalte-testen is German + EUR.
 */
const ContentTestingPage: React.FC = () => {
  const location = useLocation();
  const { t, language, setLanguage } = useLanguage();
  const match = getContentTestingFromPath(location.pathname);
  const copy = t.contentTesting;
  const counterpart = getContentTestingCounterpart(location.pathname);
  const currencyCounterpart = getContentTestingCurrencyCounterpart(location.pathname);
  const currency = match?.currency ?? 'USD';
  const pricing = withFromPrice(copy.hero.pricing, currency);
  const cta = withFromPrice(copy.hero.cta, currency);
  const applyPricing = withFromPrice(copy.applyPricing, currency);

  useIsoLayoutEffect(() => {
    if (match && match.language !== language) {
      setLanguage(match.language);
    }
  }, [match, language, setLanguage]);

  useIsoLayoutEffect(() => {
    (document.activeElement as HTMLElement | null)?.blur();
    scrollToTopInstant();
  }, [location.pathname]);

  if (!match) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="bg-gray-50" data-testid="content-testing-page" data-currency={match.currency}>
      <section className="relative bg-gradient-to-br from-primary-50 via-white to-purple-50 pt-12 pb-16 md:pt-16 md:pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
            <Link
              to={homePathFor(language)}
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4" aria-hidden="true" />
              {copy.backToHome}
            </Link>
            <div className="flex flex-wrap items-center gap-4">
              {currencyCounterpart && (
                <Link
                  to={currencyCounterpart}
                  className="text-sm font-semibold text-gray-600 hover:text-primary-600 transition-colors"
                >
                  {match.currency === 'USD' ? copy.seeEuroPrices : copy.seeUsdPrices}
                </Link>
              )}
              {counterpart && (
                <Link
                  to={counterpart}
                  className="text-sm font-semibold text-gray-600 hover:text-primary-600 transition-colors"
                  hrefLang={match.language === 'en' ? 'de' : 'en'}
                  lang={match.language === 'en' ? 'de' : 'en'}
                >
                  {copy.readInOtherLanguage}
                </Link>
              )}
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 text-balance">
            <span className="gradient-text">{copy.hero.headline}</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 mb-6 font-light text-balance">{copy.hero.subheadline}</p>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl">{copy.hero.body}</p>
          <p className="text-lg font-semibold text-gray-900 mb-8" data-testid="content-testing-pricing">
            {pricing}
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <a
              href="#apply"
              onClick={scrollToApply}
              className="inline-flex items-center justify-center bg-gradient-to-r from-primary-600 to-purple-600 text-white font-semibold px-8 py-4 rounded-xl hover:shadow-lg transition-all focus-visible"
            >
              {cta}
            </a>
            <p className="text-sm text-gray-600">{copy.hero.offer}</p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" aria-labelledby="content-testing-pillars">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 id="content-testing-pillars" className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 max-w-3xl">
            {copy.pillarsTitle}
          </h2>
          <p className="text-lg text-gray-600 mb-12 max-w-3xl">{copy.pillarsSubtitle}</p>
          <div className="grid md:grid-cols-3 gap-8">
            {copy.pillars.map((pillar, index) => {
              const Icon = index === 0 ? ComputerDesktopIcon : index === 1 ? DocumentTextIcon : FolderOpenIcon;
              return (
                <article
                  key={pillar.title}
                  className="bg-gradient-to-br from-primary-50 to-white p-8 rounded-2xl border-2 border-primary-200"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-purple-600 rounded-xl flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-white" aria-hidden="true" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{pillar.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{pillar.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-gray-50 to-primary-50" aria-labelledby="content-testing-process">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 id="content-testing-process" className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {copy.processTitle}
          </h2>
          <p className="text-lg text-gray-700 mb-12">{copy.processIntro}</p>
          <ol className="space-y-8">
            {copy.processSteps.map((step, index) => (
              <li key={step.title} className="flex gap-5">
                <span
                  className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-600 text-white font-bold flex items-center justify-center"
                  aria-hidden="true"
                >
                  {index + 1}
                </span>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="py-20 bg-white" aria-labelledby="content-testing-case">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 id="content-testing-case" className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            {copy.caseTitle}
          </h2>
          <p className="text-lg text-gray-700 mb-8 leading-relaxed">{copy.caseBody}</p>
          <div className="flex flex-wrap gap-4">
            <a
              href={IRAINBOW_WEB}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-primary-700 font-semibold hover:text-primary-800"
            >
              {copy.caseWebLabel}
              <ArrowTopRightOnSquareIcon className="w-4 h-4" aria-hidden="true" />
            </a>
            <a
              href={IRAINBOW_ORG}
              target="_blank"
              rel="noopener noreferrer"
              title="South Africa's #1 Learning Platform | iRainbow Education"
              className="inline-flex items-center gap-2 text-primary-700 font-semibold hover:text-primary-800"
            >
              {copy.caseOrgLabel}
              <ArrowTopRightOnSquareIcon className="w-4 h-4" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      <section
        id="apply"
        className="py-20 bg-gradient-to-br from-primary-50 via-white to-purple-50 scroll-mt-28"
        aria-labelledby="content-testing-apply"
      >
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-sm font-semibold text-primary-700 mb-3">{copy.applyEyebrow}</p>
              <h2 id="content-testing-apply" className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {copy.applyTitle}
              </h2>
              <p className="text-lg text-gray-700 mb-6">{copy.applyLead}</p>
              <p className="text-lg font-semibold text-gray-900 mb-8">{applyPricing}</p>
              <p className="text-sm text-gray-500 leading-relaxed">{copy.applyFinePrint}</p>
            </div>
            <ContentTestingApplyForm />
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContentTestingPage;
